// packages/ui/src/components/chat/ChatMessageBody.tsx
'use client';

import { Fragment, type ReactNode } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { parseChatMessageBody, type ChatBlock, type InlineNode, type TableAlign } from './parseChatMessageBody';

interface ChatMessageBodyProps {
  body: string;
  /** True when this is the viewer's own (right-aligned, primary-coloured) bubble
   *  — links and table chrome adapt their colour to stay legible on it. */
  own: boolean;
}

const ALIGN_TO_CSS: Record<TableAlign, 'left' | 'center' | 'right' | undefined> = {
  left: 'left',
  center: 'center',
  right: 'right',
  default: undefined,
};

function renderInline(nodes: InlineNode[], keyPrefix: string): ReactNode[] {
  return nodes.map((node, idx) => {
    const key = `${keyPrefix}.${idx}`;
    switch (node.type) {
      case 'text':
        return <Fragment key={key}>{node.value}</Fragment>;
      case 'strong':
        return <strong key={key}>{renderInline(node.children, key)}</strong>;
      case 'em':
        return <em key={key}>{renderInline(node.children, key)}</em>;
      case 'link':
        return (
          <Box
            component="a"
            key={key}
            href={node.href}
            target="_blank"
            rel="noopener noreferrer"
            sx={{
              // What MUI Link (a Typography of variant "inherit") set, and the only ones of its styles that
              // show beside the rules below: the link is a plain anchor, without Link's code in the chat
              // widget's bundle (docs/embed-size-and-speed.md §3.3 chat-link-anchor).
              m: 0,
              font: 'inherit',
              lineHeight: 'inherit',
              letterSpacing: 'inherit',
              // Inherit the bubble's text colour so the link is legible on both
              // the primary (own) and grey (incoming) backgrounds; the underline
              // + trailing icon are what mark it as a link.
              color: 'inherit',
              textDecoration: 'underline',
              fontWeight: 'inherit',
              wordBreak: 'break-word',
              overflowWrap: 'anywhere',
            }}
          >
            {renderInline(node.children, key)}
            <OpenInNewIcon
              // Decorative: target="_blank" already conveys the behaviour; the
              // icon is the visual hint that it opens a new tab.
              aria-hidden
              sx={{ fontSize: '0.9em', verticalAlign: 'text-bottom', ml: '2px' }}
            />
          </Box>
        );
    }
  });
}

/**
 * Renders a chat message body as light Markdown: **bold**, *italic*, real
 * clickable links that open in a new tab (with an open-in-new icon), and small
 * GFM tables. The table is constrained to the bubble width and scrolls
 * internally if a row is too wide, so it never widens the narrow chat panel.
 * See parseChatMessageBody.ts for the (XSS-safe, no-raw-HTML) parser.
 */
export function ChatMessageBody({ body, own }: ChatMessageBodyProps) {
  const blocks: ChatBlock[] = parseChatMessageBody(body);
  return (
    <>
      {blocks.map((block, bi) => {
        if (block.type === 'text') {
          return (
            <Typography key={`b${bi}`} variant="body2" component="div" sx={{ whiteSpace: 'pre-wrap' }}>
              {renderInline(block.nodes, `b${bi}`)}
            </Typography>
          );
        }
        const borderColor = own ? 'rgba(255,255,255,0.45)' : 'divider';
        const headBg = own ? 'rgba(255,255,255,0.14)' : 'rgba(0,0,0,0.04)';
        return (
          <Box key={`b${bi}`} sx={{ maxWidth: '100%', overflowX: 'auto', my: 0.5 }}>
            <Box
              component="table"
              sx={{
                borderCollapse: 'collapse',
                width: '100%',
                fontSize: '0.75rem',
                lineHeight: 1.35,
                '& th, & td': {
                  border: '1px solid',
                  borderColor,
                  px: 0.75,
                  py: 0.5,
                  verticalAlign: 'top',
                  whiteSpace: 'normal',
                  overflowWrap: 'anywhere',
                },
                '& th': { fontWeight: 700, bgcolor: headBg, textAlign: 'left' },
              }}
            >
              <Box component="thead">
                <Box component="tr">
                  {block.headers.map((cell, ci) => (
                    <Box key={`h${ci}`} component="th" sx={{ textAlign: ALIGN_TO_CSS[block.aligns[ci]] }}>
                      {renderInline(cell, `b${bi}.h${ci}`)}
                    </Box>
                  ))}
                </Box>
              </Box>
              <Box component="tbody">
                {block.rows.map((row, ri) => (
                  <Box key={`r${ri}`} component="tr">
                    {row.map((cell, ci) => (
                      <Box key={`c${ci}`} component="td" sx={{ textAlign: ALIGN_TO_CSS[block.aligns[ci]] }}>
                        {renderInline(cell, `b${bi}.r${ri}.c${ci}`)}
                      </Box>
                    ))}
                  </Box>
                ))}
              </Box>
            </Box>
          </Box>
        );
      })}
    </>
  );
}
